import { useState, useEffect } from 'react';
import { AppLayout } from './components/AppLayout';
import { DealHeader } from './components/DealHeader';
import { SpecUpload, CuttingDataTable } from './features/solidworks-parser';
import { CuttingCalcPanel } from './features/cutting-calc';
import { useDeal } from './features/deal-context';
//import { useNcReference, useDebugLog } from './features/reference-data';
import { useNcReference } from './features/reference-data';
import { getDealRaw, getDiskFile, fetchDiskFile } from './features/deal-context/bx24';
import { parseSpecSheet } from './features/solidworks-parser';
import type { ParsedSpec } from './features/solidworks-parser';

const SPEC_FIELD = import.meta.env.VITE_BX_SPEC_FIELD as string | undefined;

const PARENT_FOLDER_FIELD = import.meta.env.VITE_BX_PARENT_FOLDER_FIELD as string | undefined;

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase =
  | { kind: 'idle' }
  | { kind: 'loading' }      // trying to fetch spec from deal field
  | { kind: 'parsed'; spec: ParsedSpec };

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function tryLoadSpecFromDeal(dealId: string, fieldName: string): Promise<ArrayBuffer | null> {
  const deal = await getDealRaw(dealId);
  const fieldValue = deal[fieldName];

  if (import.meta.env.DEV) {
    console.debug('[spec-autoload] field value:', fieldName, fieldValue);
  }

  if (!fieldValue || fieldValue === '0' || fieldValue === 0) return null;

  const candidates = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

  for (const candidate of candidates) {
    if (!candidate || candidate === '0' || candidate === 0) continue;

    if (typeof candidate === 'object') {
      const obj = candidate as Record<string, unknown>;
      const url = (obj['DOWNLOAD_URL'] ?? obj['downloadUrl'] ?? obj['showUrl']) as string | undefined;
      if (url) {
        return fetchDiskFile(url);
      }

      const rawId = String(obj['ID'] ?? obj['id'] ?? '').replace(/^n/, '');
      const objId = parseInt(rawId, 10);
      if (Number.isFinite(objId) && objId > 0) {
        const diskFile = await getDiskFile(objId);
        if (diskFile.DOWNLOAD_URL) return fetchDiskFile(diskFile.DOWNLOAD_URL);
      }
      continue;
    }

    const raw = String(candidate).replace(/^n/, '');
    const id = parseInt(raw, 10);
    if (Number.isFinite(id) && id > 0) {
      const diskFile = await getDiskFile(id);
      if (diskFile.DOWNLOAD_URL) return fetchDiskFile(diskFile.DOWNLOAD_URL);
    }
  }

  return null;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const { data: deal } = useDeal();
  const { data: ncRef } = useNcReference();
  const ncRefOrNull = ncRef ?? null;
  // TODO: Убрать отладочные логи — начало
  //const { entries: debugEntries, clear: clearDebug } = useDebugLog();
  // TODO: Убрать отладочные логи — конец
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [autoLoadError, setAutoLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!deal || !SPEC_FIELD || phase.kind !== 'idle') return;

    setPhase({ kind: 'loading' });
    setAutoLoadError(null);

    tryLoadSpecFromDeal(deal.ID, SPEC_FIELD)
      .then((buffer) => {
        if (!buffer) {
          setPhase({ kind: 'idle' });
          return;
        }
        const result = parseSpecSheet(buffer);
        if (result.ok) {
          setPhase({ kind: 'parsed', spec: result.data });
        } else {
          setAutoLoadError(`Не удалось разобрать файл из поля сделки (${result.error.kind}). Загрузите файл вручную.`);
          setPhase({ kind: 'idle' });
        }
      })
      .catch((err: Error) => {
        if (err.message.includes('404') || err.message.includes('пусто')) {
        } else {
          setAutoLoadError(`Ошибка загрузки из поля сделки: ${err.message}. Загрузите файл вручную.`);
        }
        setPhase({ kind: 'idle' });
      });
  }, [deal?.ID]);

  const dealId = deal?.ID ?? '0';
  const parentFolderId: number | null = (() => {
    if (!deal || !PARENT_FOLDER_FIELD) return null;
    const raw = deal[PARENT_FOLDER_FIELD as `UF_CRM_${string}`];
    const n = parseInt(String(raw ?? ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  return (
    <AppLayout>
      <DealHeader />

      {autoLoadError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
          {autoLoadError}
        </div>
      )}

      {phase.kind === 'loading' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-400">
          Загружаю спецификацию из поля сделки…
        </div>
      )}

      {phase.kind === 'idle' && (
        <SpecUpload onParsed={(spec) => setPhase({ kind: 'parsed', spec })} />
      )}

      {phase.kind === 'parsed' && (
        <>
          <CuttingDataTable
            spec={phase.spec}
            onReset={() => setPhase({ kind: 'idle' })}
          />
          <CuttingCalcPanel
            spec={phase.spec}
            dealId={dealId}
            parentFolderId={parentFolderId}
            ncRef={ncRefOrNull}
          />
        </>
      )}
      {/* TODO: Убрать отладочные логи — начало */}
{/*      {debugEntries.length > 0 && (
        <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500">Отладка справочника Нч</span>
            <button
              onClick={clearDebug}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Очистить
            </button>
          </div>
          <textarea
            readOnly
            value={debugEntries.map((e) => `[${e.ts}] ${e.msg}`).join('\n')}
            className="w-full h-48 text-xs font-mono bg-white border border-gray-200 rounded p-2 resize-y"
          />
        </div>
      )}*/}
      {/* TODO: Убрать отладочные логи — конец */}
    </AppLayout>
  );
}
